import { useCallback } from "react";

import { setMessage } from "@redux/notification";

import { getFriendlyErrorMessage } from "@utils/notification";

import { type SaveOutcome, saveFailureMessage } from "../editor/persist-docx";

/**
 * The Finalise confirmation flow (MDRS-271), with the MDRS-545 save gate.
 *
 * Extracted from `ReportBuilder/index.tsx` so the ordering guarantee below can
 * be tested without rendering the whole editor. The guarantee is the point of
 * this hook: **nothing is frozen or locked unless the save fully succeeded.**
 *
 * Finalise is irreversible from the doctor's side — it renders an immutable
 * PDF from the stored .docx and locks the case. So a save that didn't land
 * means the PDF would be built from the PREVIOUS document, silently missing
 * the doctor's latest edits (and images), with no way for them to fix it.
 *
 * `handleSave` reports failure as `{ ok: false, reason }` and does NOT throw
 * for the cases that matter, which is why the original `try/catch` around it
 * never fired. All of these must block (MDRS-545 / audit RB-10):
 *
 *   `upload-failed`   the .docx blob didn't upload. The text payload and the
 *                     blob are separate requests, so the blob — the artefact
 *                     finalise actually renders, and the one carrying images —
 *                     can fail on its own.
 *   `not-serialised`  the editor produced no buffer: still lazy-loading, or
 *                     the serializer threw. Nothing was sent at all.
 *   `no-case`         no active template/case; no save was attempted.
 *   throws            network or server failure on the draft payload itself.
 *
 * The parameter type is deliberately wider than what the editor context
 * provides, so a missing or malformed result is treated as failure rather than
 * trusted — this gate gets the benefit of the doubt in exactly one direction.
 */
export type ConfirmFinaliseDeps = {
  /** `ok` only when text payload AND .docx blob both persisted. */
  handleSave: () => Promise<SaveOutcome | undefined | void>;
  finaliseMutateAsync: (args: {
    responseId: string;
    attestationVersion: string;
  }) => Promise<unknown>;
  /** Redux dispatch; only ever receives `setMessage` actions from here. */
  dispatch: (action: ReturnType<typeof setMessage>) => void;
  draftId: string | null | undefined;
  /** Called only after the server has confirmed the finalise. */
  onFinalised: () => void;
};

/** Raised when the pre-finalise save didn't land, so finalise was not attempted. */
export class PreFinaliseSaveFailedError extends Error {
  constructor() {
    super("The report was not finalised because your changes could not be saved.");
    this.name = "PreFinaliseSaveFailedError";
  }
}

export const useConfirmFinalise = ({
  handleSave,
  finaliseMutateAsync,
  dispatch,
  draftId,
  onFinalised,
}: ConfirmFinaliseDeps) =>
  useCallback(
    async (attestationVersion: string) => {
      // Save first so the .docx frozen at finalise is the current editor state.
      let outcome: SaveOutcome | undefined | void;
      let saveError: unknown;
      let threw = false;
      try {
        outcome = await handleSave();
      } catch (error) {
        threw = true;
        saveError = error;
      }

      if (threw || !outcome?.ok) {
        // ONE message carrying both halves, on EVERY failure path. The
        // notification slice is a single slot (setMessage assigns, it does not
        // stack) and Toast renders a single Snackbar, so this dispatch
        // destroys whatever the save raised. It must therefore carry the cause
        // itself — a consequence-only message would leave the doctor knowing
        // the report didn't submit but not why, and one referring to an error
        // "above" would point at nothing.
        //
        // A throw is no exception: routing the error through
        // getFriendlyErrorMessage recovers the same mapped text the save's own
        // toast would have shown (the 403 permission line, the 409 locked
        // line) rather than inventing a cause or dropping it.
        // No cast. `as Extract<..., {ok:false}>` compiled fine and then threw
        // a TypeError on the absent-result path — the one case this gate
        // exists to be paranoid about. Narrow honestly instead.
        let cause: string;
        if (threw) {
          cause = getFriendlyErrorMessage(saveError);
        } else if (outcome && !outcome.ok) {
          cause = saveFailureMessage(outcome.reason);
        } else {
          // Malformed/absent result: no cause is knowable, so say only what is.
          cause = "Your changes could not be saved.";
        }
        dispatch(
          setMessage({
            type: "error",
            content:
              `${cause} The report has NOT been submitted and nothing was ` +
              `locked — try finalising again.`,
          })
        );
        throw new PreFinaliseSaveFailedError();
      }

      if (!draftId) {
        const msg = "Save the draft before finalising.";
        dispatch(setMessage({ type: "error", content: msg }));
        throw new Error(msg);
      }

      try {
        await finaliseMutateAsync({ responseId: draftId, attestationVersion });
        onFinalised();
        dispatch(
          setMessage({
            type: "success",
            content:
              "Report submitted to MEDirect. The case is now locked and cannot be edited.",
          })
        );
      } catch (error) {
        dispatch(
          setMessage({ type: "error", content: getFriendlyErrorMessage(error) })
        );
        throw error;
      }
    },
    [handleSave, finaliseMutateAsync, dispatch, draftId, onFinalised]
  );
